"use client";

/**
 * iOS In-App Purchase hook (Apple StoreKit via Capacitor)
 *
 * Uses **cordova-plugin-purchase** (v13), which supports Capacitor and is published on npm.
 *
 * Native setup:
 *   npm install cordova-plugin-purchase
 *   npx cap sync ios
 *
 * @see https://github.com/j3k0/cordova-plugin-purchase
 */

import { useState, useCallback, useEffect, useMemo, useRef } from "react";
import { useToast } from "@/hooks/use-toast";
import { useAuthStore } from "@/stores/auth-store";
import { getApiUrl } from "@/lib/api/utils";
import { APPLE_PRODUCT_IDS, getAppleProductId } from "@/lib/payments/platform-payment";
import {
  fetchStoreKitCatalogSnapshot,
  EXPECTED_APPLE_PRODUCT_IDS,
  IAP_PLAN_IDS,
  createEmptyIapCatalogSnapshot,
  type IapCatalogSnapshot,
} from "@/lib/payments/iap-catalog";
import {
  IAP_DEBUG_LOGS,
  iapDebug,
  iapDebugWarn,
  serializeIapError,
} from "@/lib/payments/iap-debug";
import { SUBSCRIPTION_OPTIONS_UNAVAILABLE_MESSAGE } from "@/lib/payments/subscription-user-messages";

/** Runtime diagnostics for IAP debugging (paywall panel + logs). */
export type IosIapDiagnostics = {
  requestedAppleProductIds: readonly string[];
  returnedProductCount: number;
  resolvedPremiumMonthly: boolean;
  resolvedPremiumYearly: boolean;
  resolvedEliteMonthly: boolean;
  resolvedEliteYearly: boolean;
  storeInitCompleted: boolean;
  storeInitError: string | null;
  catalogFetchError: string | null;
  /** True while catalog is loading (paywall can render before this finishes). */
  iapCatalogLoading: boolean;
  purchasesBlockedNoOffers: boolean;
};

const CATALOG_TTL_MS = 60_000;

let catalogCache: { snapshot: IapCatalogSnapshot; fetchedAt: number } | null = null;
let catalogInflight: Promise<IapCatalogSnapshot> | null = null;

function invalidateCatalogCache(): void {
  catalogCache = null;
  catalogInflight = null;
}

async function getOrLoadCatalogSnapshot(
  CdvPurchase: any,
  forceRefresh: boolean,
): Promise<IapCatalogSnapshot> {
  if (forceRefresh) {
    catalogCache = null;
    catalogInflight = null;
  }
  if (
    !forceRefresh &&
    catalogCache &&
    Date.now() - catalogCache.fetchedAt < CATALOG_TTL_MS
  ) {
    iapDebug("Using cached catalog snapshot", {
      ageMs: Date.now() - catalogCache.fetchedAt,
      storeEmpty: catalogCache.snapshot.storeEmpty,
    });
    return catalogCache.snapshot;
  }
  if (!forceRefresh && catalogInflight) {
    return catalogInflight;
  }
  catalogInflight = (async () => {
    try {
      const snapshot = await fetchStoreKitCatalogSnapshot(CdvPurchase);
      catalogCache = { snapshot, fetchedAt: Date.now() };
      return snapshot;
    } finally {
      catalogInflight = null;
    }
  })();
  return catalogInflight;
}

async function getIapEnvironmentHint(): Promise<string> {
  try {
    const { Capacitor } = await import("@capacitor/core");
    return [
      `platform=${Capacitor.getPlatform()}`,
      `native=${Capacitor.isNativePlatform()}`,
      `NODE_ENV=${process.env.NODE_ENV}`,
      "note=TestFlight/sandbox uses a sandbox Apple ID; production builds use the App Store environment.",
    ].join(" ");
  } catch {
    return `NODE_ENV=${process.env.NODE_ENV}`;
  }
}

interface IAPPurchaseResult {
  success: boolean;
  transactionId?: string;
  error?: string;
  pluginMissing?: boolean;
  /** True when StoreKit returned no products or no offer for the selected plan. */
  productsUnavailable?: boolean;
}

/** Resolves when the current in-flight purchase should complete (success or failure). */
let pendingResolve: ((r: IAPPurchaseResult) => void) | null = null;
let pendingProductId: string | null = null;
/**
 * StoreKit transactionId captured from the `.approved(transaction)` event, BEFORE we
 * call `transaction.verify()`. The validator body that cordova hands us afterwards
 * derives `body.transaction.id` from `skReceipt.transactions.slice(-1)?.transactionId`,
 * which is sometimes empty (race between approval and the receipt's transactions array
 * being populated). When it's empty, the server hard-fails with 400 "Missing transactionId"
 * in production. Capturing the id at approval time and forwarding it lets the server
 * proceed with the App Store Server API verification.
 */
let pendingTransactionId: string | null = null;

let storeInitPromise: Promise<void> | null = null;
let handlersRegistered = false;

/**
 * Resolve the product ID from a validator request body.
 *
 * In cordova-plugin-purchase v13 `body.id` defaults to the bundle ID, NOT the
 * product ID — so we must check `pendingProductId` (set when we called
 * `offer.order()`) and `body.transaction.productId` before falling back.
 */
function resolveProductIdFromBody(body: {
  id?: string;
  transaction?: { productId?: string };
}): string | null {
  return (
    pendingProductId ?? body?.transaction?.productId ?? body?.id ?? null
  );
}

/**
 * Cordova plugins often attach after `deviceready`. Capacitor usually fires this early;
 * waiting reduces races where `CdvPurchase` is not yet on `window`.
 */
function waitForCordovaDeviceReady(maxMs = 10_000): Promise<void> {
  if (typeof window === "undefined" || typeof document === "undefined") {
    return Promise.resolve();
  }
  const w = window as unknown as { cordova?: { exec?: unknown } };
  if (w.cordova?.exec) return Promise.resolve();

  return new Promise((resolve) => {
    const done = () => resolve();
    const t = window.setTimeout(done, maxMs);
    document.addEventListener(
      "deviceready",
      () => {
        window.clearTimeout(t);
        done();
      },
      { once: true },
    );
  });
}

async function loadCdvPurchase(): Promise<any | null> {
  if (typeof window === "undefined") return null;
  const { Capacitor } = await import("@capacitor/core");
  if (Capacitor.getPlatform() !== "ios") return null;
  await waitForCordovaDeviceReady();

  /* No Cordova bridge means this plugin cannot work, so stop here rather than importing it
     and calling into something that is not there.
   *
   * This is not a transient condition, it is permanent in this app. cordova-plugin-purchase
   * talks to native through `window.cordova.exec`, and `window.cordova` is defined by
   * `cordova.js`, which Capacitor serves from the binary's own `public/` folder. But
   * capacitor.config.ts sets `server.url` to https://growth-sync.com, so the WebView loads
   * every page from the website and never loads that file. `deviceready` never fires either,
   * for the same reason, so the wait above always runs its full timeout first.
   *
   * Without this guard `store.initialize()` was reached anyway and threw
   * "undefined is not an object (evaluating 'window.cordova.exec')" as an UNHANDLED
   * rejection, which the global error handler turns into a crash screen. And because
   * PaywallProvider renders <Paywall /> unconditionally from the root layout, and Paywall
   * calls this hook, that happened on EVERY screen at every launch, including the welcome
   * and sign in screens where nobody is buying anything. Seen in Sentry as GROWTHSYNC-R.
   *
   * Apple purchases are therefore not working in the phone app at all today. Fixing that
   * needs the plugin to reach native, which means either bundling the web app in the binary
   * or injecting the Cordova bridge into the remote page. Out of scope here; this only stops
   * a dead code path from taking screens down with it. */
  const w = window as unknown as { cordova?: { exec?: unknown } };
  if (!w.cordova?.exec) {
    if (IAP_DEBUG_LOGS) {
      console.warn(
        "[iOS IAP] No window.cordova.exec, so the purchase plugin cannot reach native. Skipping store setup.",
      );
    }
    return null;
  }

  await import("cordova-plugin-purchase");
  const CdvPurchase = (window as unknown as { CdvPurchase?: any }).CdvPurchase ?? null;
  if (!CdvPurchase && IAP_DEBUG_LOGS) {
    console.warn(
      "[iOS IAP] CdvPurchase missing on window after cordova-plugin-purchase import — check native sync (npx cap sync ios) and Cordova bridge.",
    );
  }
  return CdvPurchase;
}

function registerStoreHandlers(CdvPurchase: any) {
  if (handlersRegistered) return;
  handlersRegistered = true;

  const store = CdvPurchase.store;

  store.validator = (body: any, callback: (payload: any) => void) => {
    void (async () => {
      try {
        iapDebug("Receipt validation request (client payload)", {
          pendingProductId,
          transactionProductId: body.transaction?.productId,
          bodyId: body.id,
        });

        // Cordova-plugin-purchase fires this validator in non-purchase contexts too —
        // e.g. when the app reloads the application-level receipt on focus changes,
        // or when the paywall dismisses mid-purchase. Without a `pendingProductId` we
        // have no active purchase to verify; calling the server with the bundle ID as
        // a fake product (which `resolveProductIdFromBody` falls back to) just
        // generates noisy 400s. Short-circuit here.
        if (!pendingProductId) {
          iapDebug("Validator called without pending purchase — skipping server roundtrip");
          callback({ ok: false, message: "No active purchase to verify" });
          return;
        }

        const productId = resolveProductIdFromBody(body);
        if (!productId) {
          callback({ ok: false, message: "Unable to resolve product ID for receipt validation" });
          return;
        }
        if (!Object.values(APPLE_PRODUCT_IDS).includes(productId)) {
          iapDebugWarn(
            "Validator using non-catalog productId (often bundle id until server resolves via transactionId).",
            { productIdSentToServer: productId },
          );
        }
        const tx = body.transaction as
          | { id?: string; transactionId?: string; productId?: string; appStoreReceipt?: string }
          | undefined;
        // Prefer the validator body's transactionId — that's derived from the LAST entry
        // of the current receipt and reliably points to the transaction the user just
        // completed. `pendingTransactionId` is a fallback for the narrow case where
        // `tx?.id` arrives empty (cordova fires the validator before the receipt's
        // transactions array is updated on fresh purchases). Originally pendingTransactionId
        // was first in this list, but that meant a stale `.approved` from an old unfinished
        // transaction (e.g. a deleted-but-still-in-receipt non-v2 subscription) could
        // overwrite the freshly captured id, causing the server to see a product mismatch.
        const storeKitTransactionId = [tx?.id, tx?.transactionId, pendingTransactionId]
          .find((v) => typeof v === "string" && v.trim() !== "") as string | undefined;
        iapDebug("Resolved transactionId sources for validator", {
          pendingTransactionId,
          txId: tx?.id,
          txTransactionId: tx?.transactionId,
          resolved: storeKitTransactionId ?? null,
        });
        // Send all three productId sources so the server can apply the
        // priority chain itself. body.id is the bundle ID in cordova-plugin-purchase
        // v13, so sending only that as `productId` masked the real IAP SKU
        // (which is available via pendingProductId or tx.productId).
        const verifyRes = await fetch(getApiUrl("/api/iap/verify-receipt"), {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({
            pendingProductId,
            transaction: tx?.productId ? { productId: tx.productId } : undefined,
            id: body.id,
            transactionId: storeKitTransactionId,
            receipt: tx?.appStoreReceipt,
          }),
        });
        const json = await verifyRes.json().catch(() => ({}));
        if (!verifyRes.ok) {
          if (IAP_DEBUG_LOGS) {
            console.warn("[iOS IAP] Receipt verification API error", {
              status: verifyRes.status,
              error: typeof json.error === "string" ? json.error : json,
              productIdSentToServer: productId,
            });
          }
          callback({
            ok: false,
            message: typeof json.error === "string" ? json.error : "Receipt verification failed",
          });
          return;
        }
        iapDebug("Receipt verification API success", {
          productIdSentToServer: productId,
          expiresAt: json.expiresAt,
        });
        const expiresAt = json.expiresAt
          ? new Date(json.expiresAt).getTime()
          : Date.now() + 30 * 86400000;
        const transactionId = storeKitTransactionId;
        callback({
          ok: true,
          data: {
            id: productId,
            latest_receipt: true,
            transaction: { type: "ios-appstore" },
            collection: [
              {
                id: productId,
                transactionId,
                purchaseDate: Date.now(),
                expiryDate: expiresAt,
                isExpired: false,
              },
            ],
          },
        });
      } catch (e: unknown) {
        const message = e instanceof Error ? e.message : "Verification failed";
        if (IAP_DEBUG_LOGS) {
          console.warn("[iOS IAP] Receipt validator exception", { message, error: String(e) });
        }
        callback({ ok: false, message });
      }
    })();
  };

  store
    .when()
    .approved((transaction: any) => {
      // Only capture the StoreKit transactionId during a user-initiated purchase.
      // cordova-plugin-purchase v13 also fires `.approved` for old unfinished
      // transactions when re-processing the receipt at app startup, on
      // visibility-change, or during auto-renewals. Without this gate, an old
      // `.approved` for a deleted non-v2 subscription overwrites the freshly
      // captured v2 transactionId and the server sees the wrong product.
      if (pendingResolve && pendingProductId) {
        const txIdFromApproval =
          typeof transaction?.transactionId === "string" && transaction.transactionId.trim() !== ""
            ? transaction.transactionId.trim()
            : null;
        if (txIdFromApproval) {
          pendingTransactionId = txIdFromApproval;
        }
      }
      transaction.verify();
    })
    .verified(async (receipt: any) => {
      const pid = pendingProductId;
      if (pendingResolve && pid) {
        // The server-side validator (above) is the source of truth — it already
        // accepted this receipt and granted the tier Apple actually returned.
        // We don't gate on whether the receipt's collection contains the
        // *requested* product because StoreKit returns the existing
        // entitlement's receipt when the Apple ID already has an active sub
        // in this subscription group (Apple's correct behaviour for plan
        // changes / re-subscribes — there's no second purchase, the existing
        // sub stands). Pick the txId for the requested SKU when present,
        // otherwise fall back to the first entry.
        const entryForPid = receipt.collection?.find(
          (p: { id: string }) => p.id === pid,
        );
        const fallbackEntry = Array.isArray(receipt.collection)
          ? receipt.collection[0]
          : undefined;
        const txId = entryForPid?.transactionId ?? fallbackEntry?.transactionId;
        iapDebug("Purchase verified (plugin)", {
          requestedProductId: pid,
          grantedFromCollection: entryForPid?.id ?? fallbackEntry?.id ?? null,
          transactionId: txId,
        });
        pendingResolve({ success: true, transactionId: txId });
      }
      await receipt.finish();
    })
    .unverified((uv: any) => {
      if (!pendingResolve) return;
      const detailed = serializeIapError(uv?.payload ?? uv);
      if (IAP_DEBUG_LOGS) {
        console.warn("[iOS IAP] Purchase unverified (plugin)", {
          ...detailed,
          productId: pendingProductId,
          rawPayload: uv?.payload,
        });
      }
      pendingResolve({
        success: false,
        error: userFacingPurchaseMessage(
          uv?.payload?.message || detailed.message,
          uv?.payload?.code,
        ),
      });
    });

  // Visible global error handler — without this, native StoreKit / plugin
  // errors are silently swallowed and the payment sheet never appears.
  store.error((err: any) => {
    const ser = serializeIapError(err);
    console.warn("[iOS IAP] store.error()", {
      message: ser.message,
      code: ser.code,
      raw: ser.raw,
      pendingProductId,
    });
    if (pendingResolve) {
      const resolve = pendingResolve;
      pendingResolve = null;
      pendingProductId = null;
      pendingTransactionId = null;
      resolve({
        success: false,
        error: userFacingPurchaseMessage(ser.message, ser.code),
      });
    }
  });
}

async function ensureIapStore(CdvPurchase: any): Promise<void> {
  if (!storeInitPromise) {
    const run = async () => {
      const store = CdvPurchase.store;
      const applePlatform = CdvPurchase.Platform.APPLE_APPSTORE;

      // 1. Handlers (validator, when().approved().verified().unverified(), error)
      //    must be wired before initialize so the plugin can dispatch events.
      registerStoreHandlers(CdvPurchase);

      // 2. Register every .v2 product BEFORE initialize. cordova-plugin-purchase
      //    v13 only fetches StoreKit metadata for products known at init time;
      //    registering later means store.ready() resolves with an empty catalog
      //    and offer.order() never opens the payment sheet.
      store.register(
        Object.values(APPLE_PRODUCT_IDS).map((id) => ({
          id,
          platform: applePlatform,
          type: CdvPurchase.ProductType.PAID_SUBSCRIPTION,
        })),
      );
      iapDebug("Registered products before initialize", {
        productIds: Object.values(APPLE_PRODUCT_IDS),
      });

      // 3. Initialize the Apple platform.
      const errors = await store.initialize([
        {
          platform: applePlatform,
          options: { needAppReceipt: true },
        },
      ]);
      if (errors?.length) {
        if (IAP_DEBUG_LOGS) {
          console.warn("[iOS IAP] Store initialize warnings:", errors);
        }
        iapDebug("Store initialize completed with platform warnings", {
          warningCount: errors.length,
          warnings: errors,
        });
      } else {
        iapDebug("Store initialized successfully (Apple App Store platform).");
      }

      // 4. Wait for store.ready() to fully resolve. Without this, calls to
      //    offer.order() race the StoreKit product load and the payment sheet
      //    never appears.
      if (typeof store.ready === "function") {
        await store.ready();
        iapDebug("Store ready resolved (products loaded).");
      }
    };
    storeInitPromise = run().catch((e) => {
      storeInitPromise = null;
      throw e;
    });
  }
  await storeInitPromise;
}

function userFacingPurchaseMessage(message?: string, code?: number): string {
  if (code === 6777006 || (message && /cancel/i.test(message))) {
    return "Purchase cancelled";
  }
  return SUBSCRIPTION_OPTIONS_UNAVAILABLE_MESSAGE;
}

function mapOrderErrorForUser(
  err: unknown,
  CdvPurchase: any,
): { userMessage: string; cancelled: boolean } {
  const code = (err as { code?: number })?.code;
  if (code === CdvPurchase.ErrorCode.PAYMENT_CANCELLED) {
    return { userMessage: "Purchase cancelled", cancelled: true };
  }
  const serialized = serializeIapError(err);
  if (IAP_DEBUG_LOGS) {
    console.warn("[iOS IAP] Purchase order failed (full detail)", {
      ...serialized,
      raw: serialized.raw,
    });
  }
  return {
    userMessage: userFacingPurchaseMessage(serialized.message, serialized.code),
    cancelled: false,
  };
}

function buildIapDiagnostics(args: {
  iapCatalog: IapCatalogSnapshot | null;
  iapCatalogLoading: boolean;
  storeInitCompleted: boolean;
  storeInitError: string | null;
}): IosIapDiagnostics {
  const { iapCatalog, iapCatalogLoading, storeInitCompleted, storeInitError } = args;
  const snap = iapCatalog;
  const purchasesBlockedNoOffers =
    !snap ||
    snap.storeEmpty ||
    !IAP_PLAN_IDS.some((id) => snap.planOfferByPlanId[id] === true);
  return {
    requestedAppleProductIds: EXPECTED_APPLE_PRODUCT_IDS,
    returnedProductCount: snap?.returnedProductIds.length ?? 0,
    resolvedPremiumMonthly: snap?.planOfferByPlanId["premium-monthly"] === true,
    resolvedPremiumYearly: snap?.planOfferByPlanId["premium-yearly"] === true,
    resolvedEliteMonthly: snap?.planOfferByPlanId["elite-monthly"] === true,
    resolvedEliteYearly: snap?.planOfferByPlanId["elite-yearly"] === true,
    storeInitCompleted,
    storeInitError,
    catalogFetchError: snap?.fetchError ?? null,
    iapCatalogLoading,
    purchasesBlockedNoOffers,
  };
}

export function useIOSIAP() {
  const [loading, setLoading] = useState(false);
  const [iapCatalog, setIapCatalog] = useState<IapCatalogSnapshot | null>(null);
  const [iapCatalogLoading, setIapCatalogLoading] = useState(false);
  const [storeInitCompleted, setStoreInitCompleted] = useState(false);
  const [storeInitError, setStoreInitError] = useState<string | null>(null);
  const loggedRequestedIdsRef = useRef(false);
  const { refreshMembership } = useAuthStore();
  const toast = useToast();

  const iapDiagnostics = useMemo(
    () =>
      buildIapDiagnostics({
        iapCatalog,
        iapCatalogLoading,
        storeInitCompleted,
        storeInitError,
      }),
    [iapCatalog, iapCatalogLoading, storeInitCompleted, storeInitError],
  );

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const CdvPurchase = await loadCdvPurchase();
      if (!CdvPurchase || cancelled) return;
      const hint = await getIapEnvironmentHint();
      iapDebug("IAP environment", { hint });
      if (!loggedRequestedIdsRef.current) {
        loggedRequestedIdsRef.current = true;
        iapDebug("Apple product IDs requested at runtime (registration)", {
          productIds: [...EXPECTED_APPLE_PRODUCT_IDS],
        });
      }
      setIapCatalogLoading(true);
      try {
        await ensureIapStore(CdvPurchase);
        if (!cancelled) {
          setStoreInitCompleted(true);
          setStoreInitError(null);
        }
        const snap = await getOrLoadCatalogSnapshot(CdvPurchase, false);
        if (!cancelled) {
          setIapCatalog(snap);
          iapDebug("Initial catalog snapshot (UI)", {
            storeEmpty: snap.storeEmpty,
            returnedProductIds: snap.returnedProductIds,
            planOfferByPlanId: snap.planOfferByPlanId,
            missingExpectedProductIds: snap.missingExpectedProductIds,
            fetchError: snap.fetchError,
          });
          iapDebug("Initial IAP diagnostics", {
            ...buildIapDiagnostics({
              iapCatalog: snap,
              iapCatalogLoading: false,
              storeInitCompleted: true,
              storeInitError: null,
            }),
          });
        }
      } catch (e) {
        const ser = serializeIapError(e);
        if (IAP_DEBUG_LOGS) {
          console.warn("[iOS IAP] Initial catalog load failed", ser);
        }
        if (!cancelled) {
          setStoreInitCompleted(false);
          setStoreInitError(ser.message);
          setIapCatalog(createEmptyIapCatalogSnapshot(ser.message));
        }
      } finally {
        if (!cancelled) setIapCatalogLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const refreshIapCatalog = useCallback(async () => {
    const CdvPurchase = await loadCdvPurchase();
    if (!CdvPurchase) return;
    setIapCatalogLoading(true);
    try {
      await ensureIapStore(CdvPurchase);
      setStoreInitCompleted(true);
      setStoreInitError(null);
      invalidateCatalogCache();
      const snap = await getOrLoadCatalogSnapshot(CdvPurchase, true);
      setIapCatalog(snap);
      iapDebug("Catalog refreshed (manual)", {
        storeEmpty: snap.storeEmpty,
        returnedProductIds: snap.returnedProductIds,
      });
    } catch (e) {
      const ser = serializeIapError(e);
      setStoreInitCompleted(false);
      setStoreInitError(ser.message);
      setIapCatalog(createEmptyIapCatalogSnapshot(ser.message));
      if (IAP_DEBUG_LOGS) {
        console.warn("[iOS IAP] refreshIapCatalog failed", ser);
      }
    } finally {
      setIapCatalogLoading(false);
    }
  }, []);

  const purchaseSubscription = useCallback(
    async (planId: string): Promise<IAPPurchaseResult> => {
      const productId = getAppleProductId(planId);
      if (!productId) {
        iapDebugWarn("Unknown planId — no Apple product mapping", {
          planId,
          knownPlanIds: IAP_PLAN_IDS,
        });
        return { success: false, error: SUBSCRIPTION_OPTIONS_UNAVAILABLE_MESSAGE };
      }

      const hint = await getIapEnvironmentHint();
      iapDebug("purchaseSubscription start", {
        planId,
        appleProductId: productId,
        environmentHint: hint,
      });

      const CdvPurchase = await loadCdvPurchase();
      if (!CdvPurchase) {
        if (IAP_DEBUG_LOGS) {
          console.warn(
            "[iOS IAP] cordova-plugin-purchase not available. Use an iOS native build and run `npx cap sync ios`.",
          );
        }
        return {
          success: false,
          pluginMissing: true,
          error: SUBSCRIPTION_OPTIONS_UNAVAILABLE_MESSAGE,
        };
      }

      setLoading(true);
      try {
        await ensureIapStore(CdvPurchase);

        if (pendingResolve) {
          return { success: false, error: "Another purchase is already in progress." };
        }

        const catalog = await getOrLoadCatalogSnapshot(CdvPurchase, false);
        iapDebug("Purchase: catalog snapshot", {
          storeEmpty: catalog.storeEmpty,
          returnedProductIds: catalog.returnedProductIds,
          selectedPlanId: planId,
          hasOfferForPlan: catalog.planOfferByPlanId[planId],
          missingExpectedProductIds: catalog.missingExpectedProductIds,
        });

        if (catalog.storeEmpty || !catalog.planOfferByPlanId[planId]) {
          if (IAP_DEBUG_LOGS) {
            console.warn("[iOS IAP] Blocking purchase — no StoreKit offer / empty catalog.", {
              planId,
              productId,
              storeEmpty: catalog.storeEmpty,
              planOfferByPlanId: catalog.planOfferByPlanId,
            });
          }
          setIapCatalog(catalog);
          return {
            success: false,
            productsUnavailable: true,
            error: SUBSCRIPTION_OPTIONS_UNAVAILABLE_MESSAGE,
          };
        }

        const store = CdvPurchase.store;
        const applePlatform = CdvPurchase.Platform.APPLE_APPSTORE;
        const product =
          store.get(productId, applePlatform) ?? store.get(productId) ?? null;
        const offer =
          product?.getOffer?.() ?? product?.offers?.[0] ?? product?.getOffer?.("$");

        if (!offer) {
          if (IAP_DEBUG_LOGS) {
            console.warn("[iOS IAP] No offer on product after non-empty catalog (unexpected).", {
              productId,
            });
          }
          return {
            success: false,
            productsUnavailable: true,
            error: SUBSCRIPTION_OPTIONS_UNAVAILABLE_MESSAGE,
          };
        }

        const result = await new Promise<IAPPurchaseResult>((resolve) => {
          const finish = (r: IAPPurchaseResult) => {
            if (timeoutHandle !== null) {
              clearTimeout(timeoutHandle);
              timeoutHandle = null;
            }
            pendingResolve = null;
            pendingProductId = null;
            pendingTransactionId = null;
            resolve(r);
          };

          pendingResolve = finish;
          pendingProductId = productId;
          pendingTransactionId = null;

          // Hard timeout: if cordova never fires .verified/.unverified/store.error
          // (StoreKit can silently swallow purchase attempts for misconfigured
          // products, leaving the UI in "Processing…" forever), bail with a clear
          // error after 30s. 30s comfortably covers the slowest expected case —
          // Apple Pay sheet ~3s + verification round-trip ~2s, with a long buffer
          // for poor connectivity. Genuine "Ask to Buy" parental-approval flows
          // take longer than this, but the right UX for that is for the user to
          // see "completed in background" rather than us hanging.
          let timeoutHandle: ReturnType<typeof setTimeout> | null = setTimeout(() => {
            iapDebugWarn(
              "Purchase pipeline timed out before verified/unverified/error fired — likely an inactive or misconfigured StoreKit product.",
              { productId, planId },
            );
            finish({
              success: false,
              productsUnavailable: true,
              error: SUBSCRIPTION_OPTIONS_UNAVAILABLE_MESSAGE,
            });
          }, 30_000);

          void (async () => {
            iapDebug("Purchase: calling offer.order()", { productId, planId });
            const err = await offer.order();
            if (err) {
              const { userMessage, cancelled } = mapOrderErrorForUser(err, CdvPurchase);
              iapDebug("Purchase: order() result", {
                productId,
                planId,
                cancelled,
                orderError: serializeIapError(err),
              });
              if (cancelled) {
                finish({ success: false, error: userMessage });
              } else {
                finish({ success: false, error: userMessage });
              }
              return;
            }
            iapDebug("Purchase order completed (awaiting verify pipeline)", { productId, planId });
          })();
        });

        if (result.success) {
          iapDebug("purchaseSubscription finished OK", {
            transactionId: result.transactionId,
            planId,
          });
          invalidateCatalogCache();
          await refreshMembership();
        } else {
          if (IAP_DEBUG_LOGS) {
            console.warn("[iOS IAP] purchaseSubscription finished with error", {
              planId,
              error: result.error,
              productsUnavailable: result.productsUnavailable,
              pluginMissing: result.pluginMissing,
            });
          }
        }
        return result;
      } catch (error: unknown) {
        const ser = serializeIapError(error);
        if (IAP_DEBUG_LOGS) {
          console.warn("[iOS IAP] purchaseSubscription exception", ser);
        }
        return {
          success: false,
          error: userFacingPurchaseMessage(ser.message, ser.code),
        };
      } finally {
        setLoading(false);
      }
    },
    [refreshMembership],
  );

  const restorePurchases = useCallback(async (): Promise<boolean> => {
    iapDebug("restorePurchases requested", {
      expectedAppleProductIds: [...EXPECTED_APPLE_PRODUCT_IDS],
    });
    const CdvPurchase = await loadCdvPurchase();
    if (!CdvPurchase) {
      iapDebugWarn("restorePurchases: plugin not available");
      toast.error(SUBSCRIPTION_OPTIONS_UNAVAILABLE_MESSAGE);
      return false;
    }

    setLoading(true);
    try {
      await ensureIapStore(CdvPurchase);
      const store = CdvPurchase.store;
      const applePlatform = CdvPurchase.Platform.APPLE_APPSTORE;
      store.register(
        Object.values(APPLE_PRODUCT_IDS).map((id) => ({
          id,
          platform: applePlatform,
          type: CdvPurchase.ProductType.PAID_SUBSCRIPTION,
        })),
      );
      const err = await CdvPurchase.store.restorePurchases();
      if (err) {
        const ser = serializeIapError(err);
        if (IAP_DEBUG_LOGS) {
          console.warn("[iOS IAP] restorePurchases: native restore returned error", ser);
        }
        toast.error(userFacingPurchaseMessage(ser.message, ser.code));
        return false;
      }
      iapDebug("restorePurchases: native restore completed, refreshing catalog");
      invalidateCatalogCache();
      await CdvPurchase.store.update();
      await refreshMembership();
      const snap = await getOrLoadCatalogSnapshot(CdvPurchase, true);
      setIapCatalog(snap);
      toast.success("Purchases restored successfully");
      return true;
    } catch (error: unknown) {
      if (IAP_DEBUG_LOGS) {
        console.warn("[iOS IAP] restorePurchases exception", { error: String(error) });
      }
      toast.error(SUBSCRIPTION_OPTIONS_UNAVAILABLE_MESSAGE);
      return false;
    } finally {
      setLoading(false);
    }
  }, [refreshMembership, toast]);

  return {
    loading,
    purchaseSubscription,
    restorePurchases,
    /** Latest StoreKit catalog snapshot (iOS native only; null on web). */
    iapCatalog,
    iapCatalogLoading,
    refreshIapCatalog,
    /** Temporary diagnostics for TestFlight / App Review debugging. */
    iapDiagnostics,
    storeInitCompleted,
    storeInitError,
  };
}
